import type { z } from "zod";
import { AsyncQueue } from "@/shared/async-queue";
import { newId } from "@/shared/prefixed-id";

// STTBase.generate()
export type STTChunk =
  | { type: "content"; textChunk: string }
  | { type: "end" }
  | { type: "error"; error: string };

export type STTJob = {
  id: string;
  stream: AsyncQueue<STTChunk>;
  cancel: () => void;
  inputVoice: (chunk: Int16Array) => void;
  _abortController: AbortController;
};

/**
 * Base class for all STT providers.
 */
export abstract class STTBase<ConfigSchema extends z.ZodObject> {
  protected config: z.infer<ConfigSchema>;

  constructor(configSchema: ConfigSchema, config: Partial<z.infer<ConfigSchema>>) {
    this.config = configSchema.parse({ ...config });
  }

  protected createGenerateJob(): STTJob {
    const id = newId("job");
    const stream = new AsyncQueue<STTChunk>();
    const _abortController = new AbortController();
    const job: STTJob = {
      id,
      stream,
      inputVoice: (pcm: Int16Array) => this.onVoiceInput(job, pcm),
      cancel: () => _abortController.abort(),
      _abortController,
    };
    return job;
  }

  // To be impemented by subclasses
  abstract generate(): Promise<STTJob>;
  protected abstract onVoiceInput(job: STTJob, pcm: Int16Array): Promise<void>;
}
